import type { TicketDto, FlightDto, SearchPayload, RouteDto } from '~/types'

export const useApi = () => {

  /**
   * Initiate a search request (POST)
   * @param params - Search parameters including origin, destination, date, etc.
   * @returns Promise<null> - Search response with status and message
   */
  const initiateSearch = async (params: SearchPayload): Promise<null> => {
    const { data, error } = await useFetch<null>('/api/search', {
      method: 'POST',
      body: params,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (error.value) {
      console.error('Error initiating search:', error.value)
      throw error.value
    }

    return data.value
  }

  /**
   * Search for flights (aggregated) based on search parameters
   * @param params - Search parameters including origin, destination, date, etc.
   * @returns Promise<FlightDto[]> - Array of flights
   */
  const searchFlightsAggregated = async (params: SearchPayload): Promise<FlightDto[]> => {
    const { data, error } = await useFetch<FlightDto[]>('/api/flights/search', {
      method: 'GET',
      query: {
        origin: params.origin,
        destination: params.destination,
        date: params.date,
        adult: params.adult,
        child: params.child,
        infant: params.infant
      }
    })

    if (error.value) {
      console.error('Error searching flights:', error.value)
      throw error.value
    }

    return Array.isArray(data.value) ? data.value : []
  }

  /**
   * Search for routes based on search parameters
   * @param params - Search parameters including origin, destination, date, etc.
   * @returns Promise<RouteDto[]> - Array of routes
   */
  const searchRoutes = async (params: SearchPayload): Promise<RouteDto[]> => {
    const { data, error } = await useFetch<RouteDto[]>('/api/routes/search', {
      method: 'GET',
      query: {
        origin: params.origin,
        destination: params.destination,
        date: params.date,
        adult: params.adult,
        child: params.child,
        infant: params.infant
      }
    })

    if (error.value) {
      console.error('Error searching routes:', error.value)
      throw error.value
    }

    return Array.isArray(data.value) ? data.value : []
  }

  /**
   * Get tickets for a specific flight
   * @param flightId - Flight ID
   * @returns Promise<TicketDto[]> - Array of tickets for the flight
   */
  const getTicketsForFlight = async (flightId: string): Promise<TicketDto[]> => {
    const { data, error } = await useFetch<TicketDto[]>(`/api/flights/${flightId}/tickets`)

    if (error.value) {
      console.error('Error getting tickets for flight:', error.value)
      throw error.value
    }

    return Array.isArray(data.value) ? data.value : []
  }

  /**
   * Poll for flight search results with automatic retry logic
   * @param params - Search parameters
   * @param maxAttempts - Maximum number of polling attempts (default: 30)
   * @param intervalMs - Polling interval in milliseconds (default: 1000)
   * @returns Promise<FlightDto[]> - Array of flights when found
   */
  const pollFlightSearchResults = async (
    params: SearchPayload,
    maxAttempts: number = 60,
    intervalMs: number = 1000
  ): Promise<FlightDto[]> => {
    let attempts = 0

    const poll = async (): Promise<FlightDto[]> => {
      try {
        const results = await searchFlightsAggregated(params)

        if (results.length > 0) {
          return results
        }

        attempts++

        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, intervalMs))
          return poll()
        } else {
          // Return empty array if no results after max attempts
          return []
        }
      } catch (error) {
        console.error(`Polling attempt ${attempts + 1} failed:`, error)
        attempts++

        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, intervalMs))
          return poll()
        } else {
          throw error
        }
      }
    }

    return poll()
  }

  /**
   * Poll for route search results with automatic retry logic
   * @param params - Search parameters
   * @param maxAttempts - Maximum number of polling attempts (default: 30)
   * @param intervalMs - Polling interval in milliseconds (default: 1000)
   * @returns Promise<RouteDto[]> - Array of routes when found
   */
  const pollRouteSearchResults = async (
    params: SearchPayload,
    maxAttempts: number = 60,
    intervalMs: number = 1000
  ): Promise<RouteDto[]> => {
    let attempts = 0

    const poll = async (): Promise<RouteDto[]> => {
      try {
        const results = await searchRoutes(params)

        if (results.length > 0) {
          return results
        }

        attempts++

        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, intervalMs))
          return poll()
        } else {
          // Return empty array if no results after max attempts
          return []
        }
      } catch (error) {
        console.error(`Polling attempt ${attempts + 1} failed:`, error)
        attempts++

        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, intervalMs))
          return poll()
        } else {
          throw error
        }
      }
    }

    return poll()
  }

  return {
    initiateSearch,
    searchFlightsAggregated,
    searchRoutes,
    getTicketsForFlight,
    pollFlightSearchResults,
    pollRouteSearchResults
  }
}
